import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Avatar } from "../components/common";
import { generatePassword } from "../lib/utils";
import { toast } from "sonner";
import {
  Copy, RefreshCw, UserPlus, Trash2, Shield, Crown, KeyRound, ShieldPlus, ShieldMinus, Lock, Unlock, Settings2, X,
} from "lucide-react";

export default function Account() {
  const { isOwner, canInvite, changePassword } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", mode: "email" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // {email, password}
  const [pw, setPw] = useState({ current: "", next: "" });
  const [managing, setManaging] = useState(null); // member being edited
  const [mgr, setMgr] = useState({ password: "", loginMode: "username", login: "" });
  const [mgrSaving, setMgrSaving] = useState(false);

  const load = async () => setData((await api.get("/account")).data);
  useEffect(() => {
    load();
  }, []);

  if (!data) return <Loading label="Loading club" />;

  const inviteLink = `${window.location.origin}/join/${data.account.invite_code}`;
  const loginUrl = `${window.location.origin}/login`;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const regenerate = async () => {
    if (!window.confirm("Generate a new code? The old code will stop working for new sign-ups."))
      return;
    const { data: r } = await api.post("/account/regenerate-invite");
    toast.success("New invite code generated");
    setData((d) => ({ ...d, account: { ...d.account, invite_code: r.invite_code } }));
  };

  const toggleLock = async () => {
    const next = !data.account.signup_locked;
    try {
      await api.put("/account/settings", { signup_locked: next });
      setData((d) => ({ ...d, account: { ...d.account, signup_locked: next } }));
      toast.success(next ? "League locked — invite link disabled" : "League unlocked — invite link active");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name };
      if (form.mode === "username") {
        payload.username = form.username;
        if (form.password.trim()) payload.password = form.password.trim();
      } else {
        payload.email = form.email;
      }
      const { data: m } = await api.post("/members", payload);
      setCreated({ login: m.login, password: m.initial_password, name: m.name, mode: m.mode });
      toast.success(`${form.name} can now log in`);
      setForm({ name: "", email: "", username: "", password: "", mode: form.mode });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name}'s login? Their player & stats stay in the club.`)) return;
    await api.delete(`/members/${m.id}`);
    toast.success("Member removed");
    load();
  };

  const setRole = async (m, role) => {
    await api.put(`/members/${m.id}/role`, { role });
    toast.success(role === "admin" ? `${m.name} is now a co-admin` : `${m.name} is now a member`);
    load();
  };

  const resetPw = async (m) => {
    if (!window.confirm(`Reset ${m.name}'s password to the club code?`)) return;
    const { data: r } = await api.post(`/members/${m.id}/reset-password`);
    toast.success(`Password reset — new password: ${r.password}`);
    setCreated({ login: m.username || m.email, password: r.password, name: m.name, reset: true, mode: m.username ? "username" : "email" });
  };

  const openManage = (m) => {
    setManaging(m);
    setMgr({
      password: "",
      loginMode: m.username ? "username" : "email",
      login: m.username || m.email || "",
    });
  };

  const saveCredentials = async () => {
    const m = managing;
    const payload = {};
    const pwd = mgr.password.trim();
    if (pwd) payload.password = pwd;
    const newLogin = mgr.login.trim();
    const origMode = m.username ? "username" : "email";
    const origLogin = m.username || m.email || "";
    if (newLogin && (mgr.loginMode !== origMode || newLogin.toLowerCase() !== origLogin.toLowerCase())) {
      payload[mgr.loginMode] = newLogin;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Set a new password or change the login first");
      return;
    }
    setMgrSaving(true);
    try {
      const { data: r } = await api.put(`/members/${m.id}/credentials`, payload);
      toast.success(`${r.name}'s login updated`);
      setCreated({ login: r.login, password: r.password || null, name: r.name, mode: r.mode, reset: true });
      setManaging(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMgrSaving(false);
    }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    try {
      await changePassword(pw.current, pw.next);
      toast.success("Password changed");
      setPw({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const roleBadge = (role) =>
    role === "owner" ? (
      <span title="Owner"><Crown className="w-4 h-4 text-[#FFCC00]" /></span>
    ) : role === "admin" ? (
      <span title="Co-admin"><Shield className="w-4 h-4 text-[#FFCC00]" /></span>
    ) : (
      <span title="Member"><Shield className="w-4 h-4 text-slate-500" /></span>
    );

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Your Club</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {data.account.name}
        </h1>
      </div>

      {canInvite && (
        <div className="panel rounded-sm p-6">
          <h2 className="font-display text-2xl font-bold uppercase text-white mb-1">Invite Friends</h2>
          <p className="text-slate-400 text-sm mb-4">
            {data.account.signup_locked
              ? 'Self sign-up is OFF — the link below won\u2019t let anyone in. Add people with "Create a Login" (the only way in), or flip the toggle to open the link.'
              : "Share this code or link. Anyone who opens it can join your club as a member."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3">
              <span className="text-xs font-bold uppercase text-slate-500">Code</span>
              <span data-testid="invite-code" className="font-display text-2xl font-black tracking-[0.3em] text-[#FFCC00]">
                {data.account.invite_code}
              </span>
            </div>
            <button data-testid="copy-code-btn" onClick={() => copy(data.account.invite_code, "Code")}
              className="flex items-center justify-center gap-2 px-4 py-3 panel rounded-sm text-white hover:border-[#007AFF] transition-colors">
              <Copy className="w-4 h-4" /> Copy Code
            </button>
            <button data-testid="copy-link-btn" onClick={() => copy(inviteLink, "Link")}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#007AFF] hover:bg-[#3395FF] rounded-sm text-white font-bold transition-colors">
              <Copy className="w-4 h-4" /> Copy Link
            </button>
            {isOwner && (
              <button data-testid="regenerate-invite-btn" onClick={regenerate} title="Generate a new code"
                className="flex items-center justify-center gap-2 px-4 py-3 panel rounded-sm text-slate-300 hover:text-[#FF3B30] transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
          {isOwner && (
            <div data-testid="lock-league-row" className="mt-4 flex items-center justify-between gap-3 border-t border-[#1c1c1c] pt-4">
              <div className="flex items-center gap-3">
                {data.account.signup_locked ? (
                  <Lock className="w-5 h-5 text-[#FF3B30] shrink-0" />
                ) : (
                  <Unlock className="w-5 h-5 text-[#34C759] shrink-0" />
                )}
                <div>
                  <div className="text-sm font-bold uppercase text-white">Lock League</div>
                  <div className="text-xs text-slate-500">
                    {data.account.signup_locked
                      ? "Invite link is off. Only you can create member logins."
                      : "Anyone with the link can join. Turn on to keep strangers out."}
                  </div>
                </div>
              </div>
              <button
                data-testid="toggle-lock-btn"
                onClick={toggleLock}
                aria-pressed={data.account.signup_locked}
                className={`relative w-14 h-7 rounded-full transition-colors duration-150 shrink-0 ${
                  data.account.signup_locked ? "bg-[#FF3B30]" : "bg-[#333]"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform duration-150 ${
                    data.account.signup_locked ? "translate-x-7" : ""
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      )}

      {canInvite && (
        <div className="panel rounded-sm p-6">
          <h2 className="font-display text-2xl font-bold uppercase text-white mb-1">Create a Login</h2>
          <p className="text-slate-400 text-sm mb-4">
            Give a league member a login. Use their email, or just a{" "}
            <span className="text-white font-semibold">username</span> (no email needed). They can
            change their password later — but don't have to.
          </p>

          <div className="grid grid-cols-2 gap-1 mb-3 max-w-xs">
            <button
              type="button"
              data-testid="mode-email-btn"
              onClick={() => setForm({ ...form, mode: "email" })}
              className={`py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${
                form.mode === "email" ? "bg-[#007AFF] text-white" : "panel text-slate-300"
              }`}
            >
              Email
            </button>
            <button
              type="button"
              data-testid="mode-username-btn"
              onClick={() => setForm({ ...form, mode: "username" })}
              className={`py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${
                form.mode === "username" ? "bg-[#007AFF] text-white" : "panel text-slate-300"
              }`}
            >
              Username
            </button>
          </div>

          <form onSubmit={addMember} className="grid sm:grid-cols-2 gap-2">
            <input data-testid="member-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Name"
              className="bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
            {form.mode === "email" ? (
              <input data-testid="member-email-input" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="Email"
                className="bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
            ) : (
              <>
                <input data-testid="member-username-input" value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })} required placeholder="Username (no spaces)"
                  className="bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
                <div className="sm:col-span-2 flex gap-2">
                  <input data-testid="member-password-input" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={`Temp password (blank = ${data.account.invite_code})`}
                    className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
                  <button type="button" data-testid="member-generate-btn"
                    onClick={() => setForm({ ...form, password: generatePassword() })}
                    className="flex items-center gap-1.5 px-3 py-3 panel rounded-sm text-slate-200 hover:text-[#34C759] hover:border-[#34C759] transition-colors whitespace-nowrap font-bold uppercase text-xs tracking-wide">
                    <RefreshCw className="w-4 h-4" /> Generate
                  </button>
                </div>
              </>
            )}
            {form.mode === "email" && (
              <p className="sm:col-span-2 text-xs text-slate-500 -mt-1">
                Temp password will be your club code{" "}
                <span className="text-[#FFCC00] font-bold">{data.account.invite_code}</span>.
              </p>
            )}
            <button data-testid="create-member-btn" disabled={saving}
              className="sm:col-span-2 flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5148] disabled:opacity-50 text-white font-bold uppercase tracking-wide py-3 rounded-sm transition-colors">
              <UserPlus className="w-4 h-4" /> {saving ? "Creating..." : "Create Login"}
            </button>
          </form>

          {created && (
            <div data-testid="created-credentials" className="mt-4 p-4 rounded-sm bg-[#0d1f12] border border-[#1c5c33]">
              <div className="text-xs font-bold uppercase tracking-wide text-[#34C759] mb-1">
                {created.reset ? "Password reset" : "Login created"} — share these with {created.name}
              </div>
              <div className="font-mono text-sm text-white">
                {created.mode === "username" ? "Username" : "Email"}: {created.login}
              </div>
              {created.password ? (
                <div className="font-mono text-sm text-white">
                  Password: <span className="text-[#FFCC00] font-bold">{created.password}</span>
                </div>
              ) : (
                <div className="font-mono text-xs text-slate-500">Password unchanged</div>
              )}
              <div className="font-mono text-sm text-white break-all">
                Sign in at: <span className="text-[#007AFF]">{loginUrl}</span>
              </div>
              <button onClick={() => copy(
                [
                  `Sign in at: ${loginUrl}`,
                  `Login: ${created.login}`,
                  ...(created.password ? [`Password: ${created.password}`] : []),
                ].join("\n"),
                "Login details"
              )}
                className="mt-2 flex items-center gap-1.5 text-xs font-bold uppercase text-[#007AFF] hover:underline">
                <Copy className="w-3.5 h-3.5" /> Copy details
              </button>
            </div>
          )}
        </div>
      )}

      {/* Members */}
      <div>
        <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">
          Members ({data.members.length})
        </h2>
        <div className="grid gap-1 sm:grid-cols-2">
          {data.members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="panel rounded-sm p-4 flex items-center gap-3">
              <Avatar name={m.name} size={44} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-bold text-white truncate">{m.name}</span>
                  {roleBadge(m.role)}
                </div>
                <div className="text-sm text-slate-400 truncate">
                  {m.username ? `@${m.username}` : m.email}
                </div>
              </div>
              {isOwner && m.id !== data.me && m.role !== "owner" && (
                <div className="flex items-center gap-1">
                  <button data-testid={`manage-login-${m.id}`} onClick={() => openManage(m)}
                    title="Manage login (set password / change username)"
                    className="text-slate-500 hover:text-[#34C759] p-1.5">
                    <Settings2 className="w-4 h-4" />
                  </button>
                  {m.role === "member" ? (
                    <button data-testid={`promote-${m.id}`} onClick={() => setRole(m, "admin")}
                      title="Make co-admin" className="text-slate-500 hover:text-[#FFCC00] p-1.5">
                      <ShieldPlus className="w-4 h-4" />
                    </button>
                  ) : (
                    <button data-testid={`demote-${m.id}`} onClick={() => setRole(m, "member")}
                      title="Remove co-admin" className="text-slate-500 hover:text-[#FFCC00] p-1.5">
                      <ShieldMinus className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`reset-pw-${m.id}`} onClick={() => resetPw(m)}
                    title="Reset password to club code" className="text-slate-500 hover:text-[#007AFF] p-1.5">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m)}
                    title="Remove login" className="text-slate-500 hover:text-[#FF3B30] p-1.5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Change my password */}
      <div className="panel rounded-sm p-6 max-w-md">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-white mb-1">
          <Lock className="w-5 h-5 text-[#007AFF]" /> Change Password
        </h2>
        <p className="text-slate-400 text-sm mb-4">Update your own login password.</p>
        <form onSubmit={submitPw} className="space-y-2">
          <input data-testid="current-password-input" type="password" value={pw.current}
            onChange={(e) => setPw({ ...pw, current: e.target.value })} required placeholder="Current password"
            className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
          <input data-testid="new-password-input" type="password" value={pw.next}
            onChange={(e) => setPw({ ...pw, next: e.target.value })} required placeholder="New password (min 6 chars)"
            className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
          <button data-testid="change-password-btn"
            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-wide py-3 rounded-sm transition-colors">
            Update Password
          </button>
        </form>
      </div>

      {!canInvite && (
        <p className="text-slate-500 text-sm">
          You're a member of this club. You can play matches and add players. Only the owner (and
          co-admins) can invite people.
        </p>
      )}

      {managing && (
        <div
          data-testid="manage-login-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setManaging(null)}
        >
          <div
            className="panel rounded-sm p-6 w-full max-w-md bg-[#111]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-2xl font-bold uppercase text-white">Manage Login</h2>
              <button data-testid="manage-close-btn" onClick={() => setManaging(null)} className="text-slate-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              For <span className="text-white font-semibold">{managing.name}</span>. Set a simple password
              and copy the details to send them.
            </p>

            <div className="space-y-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Login type</div>
                <div className="grid grid-cols-2 gap-1 mb-2">
                  <button type="button" data-testid="manage-mode-username"
                    onClick={() => setMgr({ ...mgr, loginMode: "username" })}
                    className={`py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${mgr.loginMode === "username" ? "bg-[#007AFF] text-white" : "panel text-slate-300"}`}>
                    Username
                  </button>
                  <button type="button" data-testid="manage-mode-email"
                    onClick={() => setMgr({ ...mgr, loginMode: "email" })}
                    className={`py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${mgr.loginMode === "email" ? "bg-[#007AFF] text-white" : "panel text-slate-300"}`}>
                    Email
                  </button>
                </div>
                <input
                  data-testid="manage-login-input"
                  value={mgr.login}
                  onChange={(e) => setMgr({ ...mgr, login: e.target.value })}
                  placeholder={mgr.loginMode === "username" ? "username (no spaces)" : "email address"}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                />
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">New password</div>
                <div className="flex gap-2">
                  <input
                    data-testid="manage-password-input"
                    value={mgr.password}
                    onChange={(e) => setMgr({ ...mgr, password: e.target.value })}
                    placeholder="e.g. darts123 (leave blank to keep current)"
                    className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#34C759]"
                  />
                  <button
                    type="button"
                    data-testid="manage-generate-btn"
                    onClick={() => setMgr({ ...mgr, password: generatePassword() })}
                    className="flex items-center gap-1.5 px-3 py-3 panel rounded-sm text-slate-200 hover:text-[#34C759] hover:border-[#34C759] transition-colors whitespace-nowrap font-bold uppercase text-xs tracking-wide"
                  >
                    <RefreshCw className="w-4 h-4" /> Generate
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Type your own, or hit Generate for an easy one like <span className="text-[#FFCC00] font-mono">treble47</span>.</p>
              </div>

              <button
                data-testid="manage-save-btn"
                onClick={saveCredentials}
                disabled={mgrSaving}
                className="w-full flex items-center justify-center gap-2 bg-[#34C759] hover:bg-[#2fb350] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 rounded-sm transition-colors"
              >
                <KeyRound className="w-4 h-4" /> {mgrSaving ? "Saving..." : "Save Login"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
